"use client"

// components/tracker/drugs-sheet.tsx
//
// The Drugs page as a carved sheet (Paper PZH-0, FE-DRUG-PAGE-1 Phase 4a-ii).
//
// A COPY OF companies-sheet.tsx, NOT AN IMPORT OF IT, for the reasons that
// file records about changes-sheet.tsx and trials-sheet.tsx: each sheet
// hardcodes its headline, its hero plate and its hero type. This one carries
// the PZH-0 header stack (eyebrow, title, standfirst, count line) over the
// exported Paper mesh and molecular signature. The header comment on
// changes-sheet.tsx records a rejected shared CarveSheet; this is the fourth
// copy, not a fifth abstraction.
//
// THE CARVE IS THE CHANGES CARVE, VERBATIM, drawn at the shared 1147px. PZH-0
// draws its own two notch paths (Q1V-0) on a 1421-wide, 260-tall header; the
// spec says to take them verbatim at build time, but Paper was not opened
// for this port and every tracker page shares the 1147 sheet anyway, so the
// house carve stands in. The visible hero is therefore 297 tall at the left
// (the carve's step) rather than 260.
//
// PHASE SCALE (2026-09-28): this sheet is FLUID up to 1147 and is not
// zoomed by DigestCanvas (see drugs-sheet.module.css). The SVG keeps its
// 1147x895 viewBox and path data but is drawn with
// `preserveAspectRatio="none"` into a box that is the sheet's width by 895,
// so the notches stay on the panel edges at any width and every y the type
// keys on (297, 875, 895) is unchanged.
//
// EVERY CHILD ON THE CARVE CARRIES z-index 2. The carve is z-index 1 and
// `pointer-events: none`; a child left at `auto` is painted over by the white
// region while still hit-testing as the topmost element -- present, clickable,
// invisible. `elementFromPoint` cannot tell you this; a screenshot can.

import styles from "@/components/tracker/drugs-sheet.module.css"

/** The house carve, verbatim -- the same path data companies-sheet.tsx,
 * changes-sheet.tsx and trials-sheet.tsx carry.
 *
 * The notches are PAINTED in #F8F9FA -- the page's own ground -- rather than
 * masked out, which is what makes them read as cut into the sheet. Against any
 * other background they read as pale shapes sitting on top. Change the page
 * ground and these must change with it. */
function Carve() {
  return (
    <svg
      className={styles.carve}
      width="1147"
      height="895"
      viewBox="0 0 1147 895"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M0 609.849H1029.359Q1045.401 609.849 1045.401 622.337V647.314Q1045.401 659.802 1061.443 659.802H1147V872.105Q1147 895 1117.59 895H29.41Q0 895 0 872.105Z" fill="#FFFFFF" />
      <path d="M0 312.465Q0 297.64 18.716 297.64H287.418C324.85 297.64 335.544 337.88 380.996 337.88H1147V659.802H1061.443Q1045.401 659.802 1045.401 647.095V621.679Q1045.401 608.973 1029.359 608.973H0Z" fill="#FFFFFF" />
      <path d="M-10.695 303.884H287.418C324.85 303.884 335.544 343.43 380.996 343.43H1157.695" fill="none" stroke="#F8F9FA" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M1147 101.988C1147 115.517 1114.916 115.517 1114.916 129.046V168.593C1114.916 182.122 1147 182.122 1147 195.651L1163.042 195.651 1163.042 101.988Z" fill="#F8F9FA" />
      <path d="M1147 432.93C1147 446.459 1114.916 446.459 1114.916 459.988V499.535C1114.916 513.064 1147 513.064 1147 526.593L1163.042 526.593 1163.042 432.93Z" fill="#F8F9FA" />
      <path d="M-16.042-12.488V58.279H0V52.035Q0 29.139 29.41 29.139H252.661C286.082 29.139 286.082 0 319.502 0V-12.488Z" fill="#F8F9FA" />
      <path d="M728.572-12.488V0C761.993 0 761.993 45.791 795.414 45.791H1130.958Q1147 45.791 1147 58.279V-12.488Z" fill="#F8F9FA" />
    </svg>
  )
}

/** Q1T-0. The artboard's explicit newline after "indications," is not
 * carried: the responsive rule (overview-responsive-data-layout) forbids
 * forced desktop line breaks, so the line wraps naturally in its 640 block. */
const STANDFIRST =
  "The molecules you follow, connected to their trials, indications, companies and latest activity."

export function DrugsSheet({
  countLine = null,
  body,
}: {
  /**
   * Q1U-0: "6 tracked drugs · My drug watch". Null while the read is in
   * flight -- the sheet never states a count it does not have yet.
   */
  countLine?: string | null
  /** Everything under the hero: search row, filter row, cards, scope note. */
  body?: React.ReactNode
}) {
  return (
    <div className={styles.sheet}>
      <div className={styles.hero}>
        <div className={styles.heroImage} />
        <img
          className={styles.molecularSignature}
          src="/images/drugs/drugs-molecular-signature.svg"
          alt=""
          aria-hidden="true"
        />
        {/* QKH-0: the contrast fade, layered over the plate and under the
            type exactly as the artboard stacks it. */}
        <div className={styles.heroScrim} aria-hidden="true" />

        {/* Phase SCALE: one flowing column (gap 12, the spec's own) in
            place of four absolute tops, so a title or standfirst that wraps
            pushes the lines below it down instead of painting over them.
            INSIDE the hero, so that on the rounded responsive fallback
            (drugs-sheet.module.css, the drugs-sheet container query) the
            hero's height is driven by this copy. On desktop it is absolute
            at the same 68 / 98 / 166 / 228 tops as before. */}
        <div className={styles.heroCopy}>
          <p className={styles.eyebrow}>Your workspace / Drugs</p>
          <h1 className={styles.h1}>Your tracked drugs</h1>
          <p className={styles.standfirst}>{STANDFIRST}</p>
          {countLine ? <p className={styles.countLine}>{countLine}</p> : null}
        </div>
      </div>

      <Carve />

      {/* The white extension under the carve. Absolute from 875 to the
          sheet's bottom, so it grows with the body. */}
      <div className={styles.extension} aria-hidden="true" />

      <div className={styles.body}>{body}</div>
    </div>
  )
}
